import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X } from 'lucide-react';
import useCartStore from '../store/cartStore';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const itemCount = useCartStore(state => state.getItemCount());
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Shop Honey' },
    { path: '/recipes', label: 'Recipes' }
  ];

  return (
    <header
      className={`absolute top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/80 backdrop-blur-xl border-b border-border'
          : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between py-4 md:py-6">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/5m5uevnv_Ricky%27s%20Bees%20Logo.png" 
                alt="Ricky's Bees" 
                className="h-[104px] md:h-32 w-auto"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-4">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    location.pathname === link.path
                      ? 'bg-background text-primary'
                      : 'bg-background text-primary hover:bg-background/80'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/cart"
              className="relative p-3 bg-primary rounded-full transition-colors hover:bg-primary/90"
              data-testid="cart-button"
            >
              <ShoppingBag className="w-6 h-6 text-background" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-background text-primary text-xs w-5 h-5 flex items-center justify-center rounded-full font-semibold">
                  {itemCount}
                </span>
              )}
            </Link>

            <button
              className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-6 space-y-4">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block text-lg font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-foreground/70 hover:text-primary'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
